import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CtmsWorkspaceModal from "../../common/ctms/CtmsWorkspaceModal";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import GenderSelector from "../../common/GenderSelector";
import {
  fetchEthnicityList,
  mapEthnicityOptions,
  insertVolunteer,
  getSuccessStoryRow,
} from "./participantService";
import { getParticipantAgeYears } from "./participantReportUtils";
import {
  validateContactEmail,
  validateNationalMobile,
  validateNiNumber,
  validateOptionalNhsNumber,
  validateUkPostcode,
  sanitizeNationalMobileInput,
  normalizeNationalMobileInput,
  formatNhsNumberDisplay,
  dialCodeForCountry,
  orgMobileDialLabel,
} from "../../../utils/siteFieldValidationUtils";
import {
  fetchOrganizationSetup,
  parseOrganizationSetupResponse,
} from "../../../services/organizationSetupService";
import {
  ID_NUMBER_KINDS,
  IdNumberLabel,
  useIdVerifyPrompt,
  getNhsVerifyStatusHint,
  getNiVerifyStatusHint,
} from "./participantIdNumbers";
import "./participantmanager.scss";

const EMPTY_FORM = {
  VolunteerId: "",
  ForeName: "",
  SurName: "",
  ContactNumber: "",
  EmailId: "",
  WorkEmailId: "",
  LandlineNumber: "",
  DOB: null,
  Age: "",
  Gender: "",
  NHSNumber: "",
  NINumber: "",
  IsVerify: 0,
  IsVerifyNHS: 0,
  NextofKinName: "",
  NextofKinContact: "",
  Ethnicity: "",
  EthnicityReason: "",
  AddressLine1: "",
  City: "",
  PostCode: "",
  Country: "",
  IsEmail: true,
  IsSMS: true,
  IsPost: false,
};

function toGenderLabel(value) {
  const key = String(value ?? "")
    .trim()
    .toLowerCase();
  if (key === "m" || key === "male") return "Male";
  if (key === "f" || key === "female") return "Female";
  if (key === "o" || key === "other") return "Other";
  return value ? String(value) : "";
}

function formatDobForSave(date) {
  if (!date) return "";
  const d = date instanceof Date ? date : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function newVolunteerGuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().toUpperCase();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  }).toUpperCase();
}

function validateOptionalEmail(value, label = "email") {
  const email = String(value ?? "").trim();
  if (!email) return { valid: true, message: "" };
  const result = validateContactEmail(email);
  if (result.valid) return result;
  return {
    valid: false,
    message:
      result.message === "Email is required."
        ? `Enter a valid ${label}.`
        : result.message,
  };
}

function validateOptionalPhone(value, label = "phone number", countryIso2 = "GB") {
  const phone = String(value ?? "").trim();
  if (!phone) return { valid: true, message: "", national: "" };
  const result = validateNationalMobile(phone, { countryIso2, required: false });
  if (result.valid) return result;
  return {
    valid: false,
    message:
      result.message === "Mobile number is required."
        ? `Enter a valid ${label}.`
        : result.message,
    national: "",
  };
}

function validateAddParticipantForm(form, countryIso2 = "GB") {
  const errors = {};

  if (!String(form.VolunteerId || "").trim()) {
    errors.VolunteerId = "Volunteer ID is required.";
  }

  if (!String(form.ForeName || "").trim()) {
    errors.ForeName = "First name is required.";
  }

  if (!String(form.SurName || "").trim()) {
    errors.SurName = "Last name is required.";
  }

  if (!form.DOB) {
    errors.DOB = "Date of birth is required.";
  }

  // UI "Email" is the primary address — SP requires @WorkEmailId for insert.
  const email = validateContactEmail(form.EmailId);
  if (!email.valid) {
    errors.EmailId = email.message;
  }

  const workEmail = validateOptionalEmail(form.WorkEmailId, "work email");
  if (!workEmail.valid) {
    errors.WorkEmailId = workEmail.message;
  }

  const mobile = validateNationalMobile(form.ContactNumber, {
    countryIso2,
    required: true,
  });
  if (!mobile.valid) {
    errors.ContactNumber =
      mobile.message === "Phone number is required." ||
      mobile.message === "Mobile number is required."
        ? "Mobile number is required."
        : mobile.message;
  }

  if (String(form.Ethnicity || "").toLowerCase() === "addnew") {
    if (!String(form.EthnicityReason || "").trim()) {
      errors.EthnicityReason = "Other ethnicity is required.";
    }
  }

  if (String(form.Age || "").trim()) {
    const age = Number(form.Age);
    if (!Number.isFinite(age) || age < 0 || age > 130) {
      errors.Age = "Enter a valid age (0–130).";
    }
  }

  if (String(form.PostCode || "").trim()) {
    const postcode = validateUkPostcode(form.PostCode);
    if (!postcode.valid) {
      errors.PostCode = postcode.message;
    }
  }

  const nhs = validateOptionalNhsNumber(form.NHSNumber);
  if (!nhs.valid) {
    errors.NHSNumber = nhs.message;
  }

  const ni = validateNiNumber(form.NINumber);
  if (!ni.valid) {
    errors.NINumber = ni.message;
  }

  const kinPhone = validateOptionalPhone(
    form.NextofKinContact,
    "next of kin contact",
    countryIso2,
  );
  if (!kinPhone.valid) {
    errors.NextofKinContact = kinPhone.message;
  }

  return { errors, mobileNational: mobile.national || "" };
}

function Field({
  label,
  required,
  hint,
  error,
  fieldKey,
  children,
  className = "",
}) {
  const labelIsText =
    typeof label === "string" || typeof label === "number";
  return (
    <div
      className={`pm-add-field ${className}`.trim()}
      data-pm-field={fieldKey || undefined}
    >
      <label className="pm-add-field__label">
        {label}
        {required && labelIsText ? (
          <span className="pm-add-field__req">*</span>
        ) : null}
      </label>
      {children}
      {error ? (
        <span className="pm-add-field__error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="pm-add-field__hint">{hint}</span>
      ) : null}
    </div>
  );
}

function controlClass(error) {
  return `form-control${error ? " is-invalid" : ""}`;
}

/**
 * Full-width Add Participant workspace modal (replaces the separate add screen).
 */
export default function AddParticipantModal({ open, onClose, onSaved }) {
  const navigate = useNavigate();
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [volPrefix, setVolPrefix] = useState("");
  const [ethnicityGroups, setEthnicityGroups] = useState([]);
  const [countryIso2, setCountryIso2] = useState("GB");
  const {
    VerifyModal,
    resetPrompted,
    ensureNhsVerified,
    ensureNiVerified,
    clearNhsPrompted,
    clearNiPrompted,
  } = useIdVerifyPrompt();

  const resetForm = useCallback(() => {
    setForm(EMPTY_FORM);
    setErrors({});
    setVolPrefix("");
    resetPrompted();
  }, [resetPrompted]);

  const loadLookups = useCallback(async () => {
    if (!userData?.OrganizationId) return;
    try {
      const [{ volunteerNo, ethnicities }, orgResponse] = await Promise.all([
        fetchEthnicityList(userData),
        fetchOrganizationSetup(userData.OrganizationId, userData.StaffId).catch(
          () => null,
        ),
      ]);
      const prefix = String(volunteerNo?.Prefix ?? "").trim();
      const nextId = String(
        volunteerNo?.NumberFormate ??
          volunteerNo?.numberFormate ??
          volunteerNo?.VolunteerNo ??
          volunteerNo?.EmployeeNo ??
          "",
      ).trim();
      setVolPrefix(prefix);
      setForm((prev) => ({
        ...prev,
        VolunteerId: nextId || prev.VolunteerId,
      }));
      const { groups } = mapEthnicityOptions(ethnicities, {
        includeAddNew: true,
      });
      setEthnicityGroups(groups);
      if (orgResponse && Number(orgResponse?.success) === 1) {
        const { setupRow } = parseOrganizationSetupResponse(orgResponse);
        setCountryIso2(setupRow?.CountryIso2 || "GB");
      }
    } catch {
      setEthnicityGroups([]);
    }
  }, [userData]);

  useEffect(() => {
    if (!open) return;
    resetForm();
    loadLookups();
  }, [open, loadLookups, resetForm]);

  const clearFieldError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    clearFieldError(field);
  };

  const handleDobChange = (date) => {
    const ageYears = getParticipantAgeYears(date);
    setForm((prev) => ({
      ...prev,
      DOB: date,
      Age: ageYears == null ? prev.Age : String(ageYears),
    }));
    clearFieldError("DOB");
    clearFieldError("Age");
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  const scrollToFirstError = (nextErrors) => {
    const firstKey = Object.keys(nextErrors)[0];
    if (!firstKey) return;
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-pm-field="${firstKey}"]`);
      el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    });
  };

  const handleNhsBlur = async () => {
    const result = await ensureNhsVerified(form.NHSNumber, form.IsVerifyNHS);
    if (!result.ok) {
      setErrors((prev) => ({ ...prev, NHSNumber: result.message }));
      setForm((prev) => ({ ...prev, IsVerifyNHS: 0 }));
      return;
    }
    clearFieldError("NHSNumber");
    setForm((prev) => ({ ...prev, IsVerifyNHS: result.IsVerifyNHS }));
  };

  const handleNiBlur = async () => {
    const result = await ensureNiVerified(form.NINumber, form.IsVerify);
    if (!result.ok) {
      setErrors((prev) => ({ ...prev, NINumber: result.message }));
      setForm((prev) => ({ ...prev, IsVerify: 0 }));
      return;
    }
    clearFieldError("NINumber");
    setForm((prev) => ({ ...prev, IsVerify: result.IsVerify }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { errors: nextErrors, mobileNational } = validateAddParticipantForm(
      form,
      countryIso2,
    );
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      scrollToFirstError(nextErrors);
      return;
    }

    const nhsResult = await ensureNhsVerified(form.NHSNumber, form.IsVerifyNHS);
    if (!nhsResult.ok) {
      setErrors((prev) => ({ ...prev, NHSNumber: nhsResult.message }));
      scrollToFirstError({ NHSNumber: nhsResult.message });
      return;
    }
    const niResult = await ensureNiVerified(form.NINumber, form.IsVerify);
    if (!niResult.ok) {
      setErrors((prev) => ({ ...prev, NINumber: niResult.message }));
      scrollToFirstError({ NINumber: niResult.message });
      return;
    }
    setForm((prev) => ({
      ...prev,
      IsVerifyNHS: nhsResult.IsVerifyNHS,
      IsVerify: niResult.IsVerify,
    }));

    const ethnicityValue = String(form.Ethnicity || "").trim();
    const volunteerDemRecordId = newVolunteerGuid();
    showLoader("Saving participant...");
    try {
      // Legacy SP InsertVolunteer requires ForeName + DOB + WorkEmailId.
      // UI "Email" = primary → @WorkEmailId (DB EmailId).
      // UI "Work email" = alternate → @EmailId (DB PEmailId).
      const dataset = await insertVolunteer(userData, {
        ...form,
        ContactNumber: mobileNational || form.ContactNumber,
        MobileCountryIso2: String(countryIso2 || "GB")
          .trim()
          .toUpperCase(),
        VolunteerDemRecordId: volunteerDemRecordId,
        VolunteerId: Number(form.VolunteerId) || form.VolunteerId,
        DOB: formatDobForSave(form.DOB),
        WorkEmailId: String(form.EmailId || "").trim(),
        EmailId: String(form.WorkEmailId || "").trim(),
        Ethnicity: ethnicityValue,
        EthnicityReason:
          ethnicityValue.toLowerCase() === "addnew"
            ? String(form.EthnicityReason || "").trim()
            : "",
        NHSNumber: nhsResult.digits || "",
        NINumber: niResult.ni || "",
        IsVolunteerDatabase: 0,
        IsVerify: niResult.IsVerify ? 1 : 0,
        IsVerifyNHS: nhsResult.IsVerifyNHS ? 1 : 0,
        Age: form.Age ? Number(form.Age) : 0,
      });
      const result = getSuccessStoryRow(dataset);
      const savedId =
        result?.VolunteerDemRecordId ||
        result?.volunteerDemRecordId ||
        volunteerDemRecordId;
      showAlert({
        message: result?.MSG || "Participant saved.",
        type: "success",
      });
      handleClose();
      onSaved?.(savedId);
      if (savedId) {
        navigate(`/participantmanager/participant/${savedId}`);
      }
    } catch (err) {
      showAlert({ message: err?.message || "Could not save participant." });
    } finally {
      hideLoader();
    }
  };

  return (
    <>
      <CtmsWorkspaceModal
        open={open}
        title="Add Participant"
        titleId="pm-add-participant-title"
        onClose={handleClose}
        dialogClassName="ctms-workspace-modal__dialog--full"
        contentClassName="pm-add-modal"
      >
        <form className="pm-add-modal__form" onSubmit={handleSubmit} noValidate>
          <div className="pm-add-modal__scroll">
            <div className="pm-add-modal__columns pm-add-modal__columns--3">
              <div className="pm-add-modal__col pm-add-modal__col--identity">
                <section className="pm-add-card">
                  <div className="pm-add-card__head">
                    <h2 className="pm-add-card__title">Identity</h2>
                    <p className="pm-add-card__sub">
                      Participant ID and personal details
                    </p>
                  </div>
                  <div className="pm-add-grid pm-add-grid--2">
                    <Field
                      label="Volunteer ID"
                      required
                      fieldKey="VolunteerId"
                      error={errors.VolunteerId}
                      className="pm-add-field--full"
                    >
                      <div className="pm-add-volid">
                        <input
                          className="form-control pm-add-volid__prefix"
                          value={volPrefix}
                          disabled
                          readOnly
                          aria-label="Volunteer ID prefix"
                        />
                        <input
                          className={controlClass(errors.VolunteerId)}
                          value={form.VolunteerId}
                          onChange={(e) =>
                            updateField(
                              "VolunteerId",
                              e.target.value.replace(/[^\d]/g, ""),
                            )
                          }
                          inputMode="numeric"
                          required
                          aria-label="Volunteer ID number"
                          aria-invalid={Boolean(errors.VolunteerId)}
                        />
                      </div>
                    </Field>
                    <Field
                      label="First name"
                      required
                      fieldKey="ForeName"
                      error={errors.ForeName}
                    >
                      <input
                        className={controlClass(errors.ForeName)}
                        value={form.ForeName}
                        onChange={(e) =>
                          updateField("ForeName", e.target.value)
                        }
                        autoComplete="given-name"
                        required
                        aria-invalid={Boolean(errors.ForeName)}
                      />
                    </Field>
                    <Field
                      label="Last name"
                      required
                      fieldKey="SurName"
                      error={errors.SurName}
                    >
                      <input
                        className={controlClass(errors.SurName)}
                        value={form.SurName}
                        onChange={(e) => updateField("SurName", e.target.value)}
                        autoComplete="family-name"
                        required
                        aria-invalid={Boolean(errors.SurName)}
                      />
                    </Field>
                    <Field
                      label="Date of birth"
                      required
                      fieldKey="DOB"
                      error={errors.DOB}
                    >
                      <DatePicker
                        selected={form.DOB}
                        onChange={handleDobChange}
                        placeholderText="Select date"
                        className={controlClass(errors.DOB)}
                        dateFormat="dd/MM/yyyy"
                        isClearable
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        maxDate={new Date()}
                        onChangeRaw={(e) => e.preventDefault()}
                        popperProps={{ strategy: "fixed" }}
                      />
                    </Field>
                    <Field
                      label="Age"
                      hint="Calculated from date of birth"
                      fieldKey="Age"
                      error={errors.Age}
                    >
                      <input
                        type="number"
                        className={controlClass(errors.Age)}
                        value={form.Age}
                        onChange={(e) => updateField("Age", e.target.value)}
                        min={0}
                        max={130}
                        aria-invalid={Boolean(errors.Age)}
                      />
                    </Field>
                    <Field label="Gender">
                      <GenderSelector
                        value={form.Gender || null}
                        onChange={(value) =>
                          updateField("Gender", toGenderLabel(value))
                        }
                        size="medium"
                      />
                    </Field>
                    <Field label="Ethnicity">
                      <select
                        className="form-control"
                        value={form.Ethnicity}
                        onChange={(e) => {
                          const next = e.target.value;
                          setForm((prev) => ({
                            ...prev,
                            Ethnicity: next,
                            EthnicityReason:
                              next.toLowerCase() === "addnew"
                                ? prev.EthnicityReason
                                : "",
                          }));
                          clearFieldError("EthnicityReason");
                        }}
                      >
                        <option value="">Select</option>
                        {ethnicityGroups.map((group) => (
                          <optgroup key={group.label} label={group.label}>
                            {group.options.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                        <option value="AddNew">Add New</option>
                      </select>
                    </Field>
                    {String(form.Ethnicity).toLowerCase() === "addnew" ? (
                      <Field
                        label="Other Ethnicity"
                        required
                        fieldKey="EthnicityReason"
                        error={errors.EthnicityReason}
                      >
                        <input
                          type="text"
                          className={controlClass(errors.EthnicityReason)}
                          value={form.EthnicityReason}
                          onChange={(e) =>
                            updateField("EthnicityReason", e.target.value)
                          }
                          placeholder="Other Ethnicity"
                          aria-invalid={Boolean(errors.EthnicityReason)}
                        />
                      </Field>
                    ) : null}
                  </div>
                </section>

                <section className="pm-add-card">
                  <div className="pm-add-card__head">
                    <h2 className="pm-add-card__title">Preferences</h2>
                    <p className="pm-add-card__sub">
                      Communication channels and database consent
                    </p>
                  </div>
                  <div className="pm-contact-prefs pm-add-prefs">
                    {[
                      { key: "IsEmail", label: "Receive Email" },
                      { key: "IsSMS", label: "Receive SMS" },
                      { key: "IsPost", label: "Receive Post" },
                    ].map(({ key, label }) => (
                      <label key={key} className="pm-contact-prefs__item">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={Boolean(form[key])}
                          onChange={(e) => updateField(key, e.target.checked)}
                        />
                        <span className="pm-contact-prefs__label">{label}</span>
                      </label>
                    ))}
                  </div>
                </section>
              </div>

              <div className="pm-add-modal__col">
                <section className="pm-add-card">
                  <div className="pm-add-card__head">
                    <h2 className="pm-add-card__title">Contact</h2>
                    <p className="pm-add-card__sub">
                      How the site can reach this person
                    </p>
                  </div>
                  <div className="pm-add-grid pm-add-grid--2">
                    <Field
                      label="Email"
                      required
                      fieldKey="EmailId"
                      error={errors.EmailId}
                    >
                      <input
                        type="email"
                        className={controlClass(errors.EmailId)}
                        value={form.EmailId}
                        onChange={(e) => updateField("EmailId", e.target.value)}
                        autoComplete="email"
                        required
                        aria-invalid={Boolean(errors.EmailId)}
                      />
                    </Field>
                    <Field
                      label="Work email"
                      fieldKey="WorkEmailId"
                      error={errors.WorkEmailId}
                    >
                      <input
                        type="email"
                        className={controlClass(errors.WorkEmailId)}
                        value={form.WorkEmailId}
                        onChange={(e) =>
                          updateField("WorkEmailId", e.target.value)
                        }
                        aria-invalid={Boolean(errors.WorkEmailId)}
                      />
                    </Field>
                    <Field
                      label="Mobile number"
                      required
                      fieldKey="ContactNumber"
                      error={errors.ContactNumber}
                      hint="Digits only. Leading 0 is removed automatically."
                      className="pm-add-field--full"
                    >
                      <div className="pm-phone-input">
                        <select
                          className="form-control pm-phone-input__dial"
                          value={orgMobileDialLabel(countryIso2)}
                          disabled
                          aria-label="Organisation country dial code"
                        >
                          <option value={orgMobileDialLabel(countryIso2)}>
                            {orgMobileDialLabel(countryIso2)}
                          </option>
                        </select>
                        <input
                          className={controlClass(errors.ContactNumber)}
                          value={form.ContactNumber}
                          onChange={(e) => {
                            const { national, wrongDial } =
                              normalizeNationalMobileInput(
                                e.target.value,
                                countryIso2,
                              );
                            updateField("ContactNumber", national);
                            if (wrongDial) {
                              setErrors((prev) => ({
                                ...prev,
                                ContactNumber: `Mobile number must use the organisation country dial code (${dialCodeForCountry(countryIso2)}).`,
                              }));
                            } else {
                              clearFieldError("ContactNumber");
                            }
                          }}
                          onPaste={(e) => {
                            const text = e.clipboardData?.getData("text") ?? "";
                            e.preventDefault();
                            const { national, wrongDial } =
                              normalizeNationalMobileInput(text, countryIso2);
                            updateField("ContactNumber", national);
                            if (wrongDial) {
                              setErrors((prev) => ({
                                ...prev,
                                ContactNumber: `Mobile number must use the organisation country dial code (${dialCodeForCountry(countryIso2)}).`,
                              }));
                            } else {
                              clearFieldError("ContactNumber");
                            }
                          }}
                          inputMode="numeric"
                          autoComplete="tel-national"
                          required
                          aria-invalid={Boolean(errors.ContactNumber)}
                          placeholder="7123456789"
                        />
                      </div>
                    </Field>
                    <Field label="Address" className="pm-add-field--full">
                      <textarea
                        className="form-control"
                        rows={5}
                        value={form.AddressLine1}
                        onChange={(e) =>
                          updateField("AddressLine1", e.target.value)
                        }
                        autoComplete="street-address"
                      />
                    </Field>
                    <Field label="City">
                      <input
                        className="form-control"
                        value={form.City}
                        onChange={(e) => updateField("City", e.target.value)}
                        autoComplete="address-level2"
                      />
                    </Field>
                    <Field
                      label="Post code"
                      fieldKey="PostCode"
                      error={errors.PostCode}
                    >
                      <input
                        className={controlClass(errors.PostCode)}
                        value={form.PostCode}
                        onChange={(e) =>
                          updateField("PostCode", e.target.value)
                        }
                        autoComplete="postal-code"
                        aria-invalid={Boolean(errors.PostCode)}
                      />
                    </Field>
                    <Field label="Country" className="pm-add-field--full">
                      <input
                        className="form-control"
                        value={form.Country}
                        onChange={(e) => updateField("Country", e.target.value)}
                        autoComplete="country-name"
                      />
                    </Field>
                  </div>
                </section>
              </div>

              <div className="pm-add-modal__col">
                <section className="pm-add-card">
                  <div className="pm-add-card__head">
                    <h2 className="pm-add-card__title">
                      Identifiers &amp; next of kin
                    </h2>
                    <p className="pm-add-card__sub">
                      Optional NHS / NI and emergency contact
                    </p>
                  </div>
                  <div className="pm-add-grid pm-add-grid--1">
                    <Field
                      label={
                        <IdNumberLabel kind={ID_NUMBER_KINDS.NHS}>
                          NHS number
                        </IdNumberLabel>
                      }
                      fieldKey="NHSNumber"
                      error={errors.NHSNumber}
                      hint={
                        !errors.NHSNumber
                          ? getNhsVerifyStatusHint(
                              form.NHSNumber,
                              form.IsVerifyNHS,
                            ) || undefined
                          : undefined
                      }
                    >
                      <input
                        className={controlClass(errors.NHSNumber)}
                        value={form.NHSNumber}
                        onChange={(e) => {
                          clearNhsPrompted();
                          setForm((prev) => ({
                            ...prev,
                            NHSNumber: formatNhsNumberDisplay(e.target.value),
                            IsVerifyNHS: 0,
                          }));
                          clearFieldError("NHSNumber");
                        }}
                        onBlur={handleNhsBlur}
                        inputMode="numeric"
                        maxLength={12}
                        aria-invalid={Boolean(errors.NHSNumber)}
                      />
                    </Field>
                    <Field
                      label={
                        <IdNumberLabel kind={ID_NUMBER_KINDS.NI}>
                          NI number
                        </IdNumberLabel>
                      }
                      fieldKey="NINumber"
                      error={errors.NINumber}
                      hint={
                        !errors.NINumber
                          ? getNiVerifyStatusHint(
                              form.NINumber,
                              form.IsVerify,
                            ) || undefined
                          : undefined
                      }
                    >
                      <input
                        className={controlClass(errors.NINumber)}
                        value={form.NINumber}
                        onChange={(e) => {
                          clearNiPrompted();
                          setForm((prev) => ({
                            ...prev,
                            NINumber: e.target.value.toUpperCase(),
                            IsVerify: 0,
                          }));
                          clearFieldError("NINumber");
                        }}
                        onBlur={handleNiBlur}
                        maxLength={13}
                        aria-invalid={Boolean(errors.NINumber)}
                      />
                    </Field>
                    <Field label="Next of kin name">
                      <input
                        className="form-control"
                        value={form.NextofKinName}
                        onChange={(e) =>
                          updateField("NextofKinName", e.target.value)
                        }
                      />
                    </Field>
                    <Field
                      label="Next of kin contact"
                      fieldKey="NextofKinContact"
                      error={errors.NextofKinContact}
                    >
                      <input
                        className={controlClass(errors.NextofKinContact)}
                        value={form.NextofKinContact}
                        onChange={(e) =>
                          updateField(
                            "NextofKinContact",
                            sanitizeNationalMobileInput(
                              e.target.value,
                              countryIso2,
                            ),
                          )
                        }
                        onPaste={(e) => {
                          const text = e.clipboardData?.getData("text") ?? "";
                          e.preventDefault();
                          updateField(
                            "NextofKinContact",
                            sanitizeNationalMobileInput(text, countryIso2),
                          );
                        }}
                        inputMode="numeric"
                        aria-invalid={Boolean(errors.NextofKinContact)}
                      />
                    </Field>
                  </div>
                </section>
              </div>
            </div>
          </div>

          <footer className="pm-add-modal__footer">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              <i className="fas fa-user-plus me-1" aria-hidden="true" />
              Save Participant
            </button>
          </footer>
        </form>
      </CtmsWorkspaceModal>

      {VerifyModal}
      <InfoAlert {...alertState} onClose={hideAlert} />
    </>
  );
}
